import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { IListItem } from "components/List/List"
import { useAssetPinnedFields } from "stores/assetPinnedFields"
import Asset from "models/Asset"
import NoValue from "components/NoValue/NoValue"
import { formatNumber } from "utils/utils"
import { ArticleAny } from "@/types"
import ColorModel from "models/Color"
import Color from "components/Color/Color"
import { useUnits } from "stores/units"
import { ArticleEquipment } from "models/ArticleEquipment"
import { ArticleZts } from "models/ArticleZts"
import useTechnicalData from "hooks/useTechnicalData"
import { GlobalAssetOverviewAsset } from "models/GlobalAssetOverviewAsset"

export enum AssetTechnicalDataField {
    color = "color",
    emissionLevel = "emissionLevel",
    dimensions = "dimensions",
    volume = "volume",
    weight = "weight",
    quickCouplingSystem = "quickCouplingSystem",
    connectionType = "connectionType",
    boltOnCuttingEdge = "boltOnCuttingEdge",
    teeth = "teeth",
    heavyDuty = "heavyDuty",
    locking = "locking",
    controlSystem = "controlSystem",
    agility = "agility",
}


export default function useAssetTechnicalDataFields({
    asset,
    article,
    color,
    pinnable,
}: {
    asset: Asset | GlobalAssetOverviewAsset | null
    article: ArticleAny
    color?: ColorModel | null
    pinnable?: boolean
}) {
    const { t } = useTranslation()
    const { pinnedFields, toggleFieldPin } = useAssetPinnedFields()

    const { getUnit } = useUnits()
    const assetUnit = getUnit(asset?.measurementSystemId || undefined)
    const articleUnit = getUnit(
        article && 'measurementSystemId' in article && article.measurementSystemId
            ? article?.measurementSystemId
            : undefined
    )

    const {
        QuickCouplingSystemTypes,
        ConnectionTypes,
        LockingTypes,
        ControlSystemTypes,
        AgilityTypes,
    } = useTechnicalData()

    const getField = useCallback((field: AssetTechnicalDataField): IListItem | null => {
        if (!asset || !article) return null

        const pinned = pinnedFields?.includes(field) || false
        const onPinnedToggle = pinnable ? () => toggleFieldPin(field) : undefined

        switch (field) {
            case AssetTechnicalDataField.color:
                return {
                    label: t('colors.color'),
                    value: color ? <Color color={color} condensed reversed /> : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.emissionLevel:
                return {
                    label: t('asset.emissionLevel'),
                    value: asset.emissionLevel || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.dimensions:
                const assetUnitLabel = assetUnit?.printShort() || ""
                let dimensions = [
                    typeof asset?.length === "number" ? `${t('common.dimensionShort.length')} ${formatNumber(asset.length)} ${assetUnitLabel}` : null,
                    typeof asset?.width === "number" ? `${t('common.dimensionShort.width')} ${formatNumber(asset.width)} ${assetUnitLabel}` : null,
                    typeof asset?.height === "number" ? `${t('common.dimensionShort.height')} ${formatNumber(asset.height)} ${assetUnitLabel}` : null
                ].filter(d => !!d)
                let dimensionsAreFromArticle = false
                if (dimensions.length === 0 && (article instanceof ArticleEquipment || article instanceof ArticleZts)) {
                    const articleUnitLabel = articleUnit?.printShort() || ""
                    dimensions = [
                        typeof article.length === "number" ? `${t('common.dimensionShort.length')} ${formatNumber(article.length)} ${articleUnitLabel}` : null,
                        typeof article.width === "number" ? `${t('common.dimensionShort.width')} ${formatNumber(article.width)} ${articleUnitLabel}` : null,
                        typeof article.height === "number" ? `${t('common.dimensionShort.height')} ${formatNumber(article.height)} ${articleUnitLabel}` : null
                    ].filter(d => !!d)
                    if (dimensions.length > 0) {
                        dimensionsAreFromArticle = true
                    }
                }
                return {
                    label: t('common.dimensions'),
                    value: dimensions.length > 0
                        ? <div className={dimensionsAreFromArticle ? 'color-grey-500 font-regular' : ''}>{dimensions.join(" x ")}</div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.volume:
                let volume = asset?.volume
                let volumeIsFromArticle = false
                if (typeof volume !== "number" && (article instanceof ArticleEquipment || article instanceof ArticleZts)) {
                    volume = article.volume
                    if (typeof volume === "number") {
                        volumeIsFromArticle = true
                    }
                }
                return {
                    label: t('common.volume'),
                    value: typeof volume === "number"
                        ? <div className={volumeIsFromArticle ? 'color-grey-500 font-regular' : ''}>
                            {formatNumber(volume) + " " + t('common.m3')}
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.weight:
                let weight = asset?.weight
                let weightIsFromArticle = false
                if (typeof weight !== "number" && (article instanceof ArticleEquipment || article instanceof ArticleZts)) {
                    weight = article.weight
                    if (typeof weight === "number") {
                        weightIsFromArticle = true
                    }
                }
                return {
                    label: t('common.weight'),
                    value: typeof weight === "number"
                        ? <div className={weightIsFromArticle ? 'color-grey-500 font-regular' : ''}>
                            {formatNumber(weight) + " " + t('common.kg')}
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.quickCouplingSystem:
                if (!(article instanceof ArticleEquipment)) return null
                return {
                    label: t('asset.qcQuickCoupler'),
                    value: asset.quickCouplingSystem || article.quickCouplingSystem
                        ? <div className={!asset.quickCouplingSystem && article.quickCouplingSystem ? 'color-grey-500 font-regular' : ''}>
                            {asset.quickCouplingSystem
                                ? (QuickCouplingSystemTypes.get(asset.quickCouplingSystem) || asset.quickCouplingSystem)
                                : (article.quickCouplingSystem ? (QuickCouplingSystemTypes.get(article.quickCouplingSystem) || article.quickCouplingSystem) : "")
                            }
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.connectionType:
                if (!(article instanceof ArticleEquipment)) return null
                return {
                    label: t('asset.connectionType'),
                    value: asset.connectionType || article.connectionType
                        ? <div className={!asset.connectionType && article.connectionType ? 'color-grey-500 font-regular' : ''}>
                            {asset.connectionType
                                ? (ConnectionTypes.get(asset.connectionType) || asset.connectionType)
                                : (article.connectionType ? (ConnectionTypes.get(article.connectionType) || article.connectionType) : "")
                            }
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.boltOnCuttingEdge:
                if (!(article instanceof ArticleEquipment)) return null
                const cuttingEdge = typeof asset.cuttingEdge === "boolean"
                    ? asset.cuttingEdge
                    : article.cuttingEdge
                const cuttingEdgeFromArticle = typeof article.cuttingEdge === "boolean"
                    && typeof asset.cuttingEdge !== "boolean"
                return {
                    label: t('asset.bocBoltOnCuttingEdge'),
                    value: typeof cuttingEdge === "boolean"
                        ? <div className={cuttingEdgeFromArticle ? 'color-grey-500 font-regular' : ''}>
                            {cuttingEdge ? t('common.yes') : t('common.no')}
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.teeth:
                if (!(article instanceof ArticleEquipment)) return null
                const teeth = typeof asset.teeth === "boolean"
                    ? asset.teeth
                    : article.teeth
                const teethFromArticle = typeof article.teeth === "boolean"
                    && typeof asset.teeth !== "boolean"
                return {
                    label: t('asset.teeth'),
                    value: typeof teeth === "boolean"
                        ? <div className={teethFromArticle ? 'color-grey-500 font-regular' : ''}>
                            {teeth ? t('common.yes') : t('common.no')}
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.heavyDuty:
                if (!(article instanceof ArticleEquipment)) return null
                const heavyDuty = typeof asset.heavyDuty === "boolean"
                    ? asset.heavyDuty
                    : article.heavyDuty
                const heavyDutyFromArticle = typeof article.heavyDuty === "boolean"
                    && typeof asset.heavyDuty !== "boolean"
                return {
                    label: t('asset.hdHeavyDuty'),
                    value: typeof heavyDuty === "boolean"
                        ? <div className={heavyDutyFromArticle ? 'color-grey-500 font-regular' : ''}>
                            {heavyDuty ? t('common.yes') : t('common.no')}
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.locking:
                if (!(article instanceof ArticleEquipment)) return null
                return {
                    label: t('asset.locking'),
                    value: asset.locking || article.locking
                        ? <div className={!asset.locking && article.locking ? 'color-grey-500 font-regular' : ''}>
                            {asset.locking
                                ? (LockingTypes.get(asset.locking) || asset.locking)
                                : (article.locking ? (LockingTypes.get(article.locking) || article.locking) : "")
                            }
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.controlSystem:
                if (!(article instanceof ArticleEquipment)) return null
                return {
                    label: t('asset.controlSystem'),
                    value: asset.controlSystem || article.controlSystem
                        ? <div className={!asset.controlSystem && article.controlSystem ? 'color-grey-500 font-regular' : ''}>
                            {asset.controlSystem
                                ? (ControlSystemTypes.get(asset.controlSystem) || asset.controlSystem)
                                : (article.controlSystem ? (ControlSystemTypes.get(article.controlSystem) || article.controlSystem) : "")
                            }
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.agility:
                if (!(article instanceof ArticleEquipment)) return null
                return {
                    label: t('asset.agility'),
                    value: asset.agility || article.agility
                        ? <div className={!asset.agility && article.agility ? 'color-grey-500 font-regular' : ''}>
                            {asset.agility
                                ? (AgilityTypes.get(asset.agility) || asset.agility)
                                : (article.agility ? (AgilityTypes.get(article.agility) || article.agility) : "")
                            }
                        </div>
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            default:
                return null
        }
    }, [AgilityTypes, ConnectionTypes, ControlSystemTypes, LockingTypes, QuickCouplingSystemTypes, article, articleUnit, asset, assetUnit, color, pinnable, pinnedFields, t, toggleFieldPin])

    const getFields = useCallback((fields: AssetTechnicalDataField[]) => {
        const items: IListItem[] = []
        for (const field of fields) {
            const item = getField(field)
            if (!item) continue
            items.push(item)
        }
        return items
    }, [getField])

    return {
        getField,
        getFields,
    }
}